// src/admin/runsApi.js
//
// BE endpoints:
//   GET /api/v1/pipeline/runs           — tenant-scoped list
//   GET /api/v1/pipeline/run/{task_id}  — full run detail (brief + result + scalars)
//
// We do NOT reuse backendClient.getPipelineRunStatus / getPipelineRunContent
// because those use raw fetch and bypass apiFetch's 401/403 handling.
// suppressTenantHeader is set to true here so that an explicit ?tenant=
// wins over any stale X-Tenant-Id in localStorage (matters for hybrid SAs).
//
import { apiFetch } from '../auth/apiFetch.js';

const PIPELINE = '/api/v1/pipeline';

function toIsoNaiveUtc(dateStr) {
  // dateStr from <input type="date"> is YYYY-MM-DD
  if (!dateStr) return null;
  return `${dateStr}T00:00:00`;
}

// BE treats `before` as EXCLUSIVE upper bound on created_at, so for a selected
// date (intended as inclusive last day of the range) we must send start-of-NEXT-day.
// Example: user picks 2026-05-11 → send 2026-05-12T00:00:00.
function toIsoNaiveUtcExclusiveBefore(dateStr) {
  if (!dateStr) return null;
  // Parse YYYY-MM-DD as a UTC date to avoid TZ drift, then add one day.
  const [y, m, d] = dateStr.split('-').map(n => parseInt(n, 10));
  if (!y || !m || !d) return null;
  const next = new Date(Date.UTC(y, m - 1, d));
  next.setUTCDate(next.getUTCDate() + 1);
  const yyyy = next.getUTCFullYear();
  const mm = String(next.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(next.getUTCDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}T00:00:00`;
}

/**
 * List pipeline runs.
 * Params here follow the Phase-0-verified shape — adjust names if BE differs.
 */
export async function listRuns({
  tenant, status, platform, contentFormat, since, before, user,
  limit = 20, offset = 0,
} = {}) {
  const params = new URLSearchParams();
  if (tenant) params.set('tenant', tenant);
  if (Array.isArray(status) && status.length > 0) params.set('status', status.join(','));
  if (platform) params.set('platform', platform);
  if (contentFormat) params.set('content_format', contentFormat);
  const sinceIso = toIsoNaiveUtc(since);
  const beforeIso = toIsoNaiveUtcExclusiveBefore(before);
  if (sinceIso) params.set('since', sinceIso);
  if (beforeIso) params.set('before', beforeIso);
  if (user) params.set('user', user);
  params.set('limit', String(limit));
  params.set('offset', String(offset));

  const res = await apiFetch(`${PIPELINE}/runs?${params}`, { suppressTenantHeader: true });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    if (body.error_code === 'UNKNOWN_QUERY_PARAM') {
      throw new Error(`Parametro non riconosciuto: ${body.message || res.status}`);
    }
    throw new Error(body.message || `Failed to list runs: ${res.status}`);
  }
  return res.json();
}

/**
 * Fetch full pipeline run detail — the single endpoint that returns
 * brief_json + result_json + all scalars/scores/timestamps.
 * Replaces the previous two-fetch pattern (getRunStatus + getRunContent).
 *
 * Returns null on 404 so callers can render "Run non trovata" gracefully.
 * suppressTenantHeader: true — superadmins viewing cross-tenant runs must
 * not have a stale X-Tenant-Id override the BE's row-level auth.
 */
export async function getRunDetail(taskId) {
  const res = await apiFetch(`${PIPELINE}/run/${taskId}`, { suppressTenantHeader: true });
  if (res.status === 404) return null;
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message || `Failed to fetch run detail: ${res.status}`);
  }
  return res.json();
}
